<template>
  <div class="h-full">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Comparable Data MySQL Manager</h1>
      <p class="text-gray-600">Manage MySQL operations for comparable_data database</p>
    </div>

    <!-- Connection Status -->
    <!-- <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div class="w-3 h-3 rounded-full mr-3" :class="connectionStatus ? 'bg-green-500' : 'bg-red-500'"></div>
          <span class="font-medium">{{ connectionStatus ? 'Connected' : 'Disconnected' }}</span>
        </div>
        <button 
          @click="toggleConnection"
          class="px-4 py-2 rounded-lg border transition-colors"
          :class="connectionStatus ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-600 hover:bg-green-50'"
        >
          {{ connectionStatus ? 'Disconnect' : 'Connect' }}
        </button>
      </div>
    </div> -->

    <!-- Database Operations -->
    <div class="hidden grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <!-- Table Management -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 class="text-lg font-semibold mb-4 flex items-center">
          <i class="fas fa-table mr-2 text-blue-500"></i>
          Table Management
        </h3>
        <div class="space-y-3">
          <button 
            @click="showTables"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-list mr-2"></i>
            Show Tables
          </button>
          <button 
            @click="analyzeTable"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-search mr-2"></i>
            Analyze Tables
          </button>
          <button 
            @click="optimizeTable"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-tools mr-2"></i>
            Optimize Tables
          </button>
        </div>
      </div>

      <!-- Data Operations -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <h3 class="text-lg font-semibold mb-4 flex items-center">
          <i class="fas fa-database mr-2 text-green-500"></i>
          Data Operations
        </h3>
        <div class="space-y-3">
          <button 
            @click="queryData"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-search mr-2"></i>
            Query Data
          </button>
          <button 
            @click="exportData"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-download mr-2"></i>
            Export Data
          </button>
          <button 
            @click="importData"
            class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-upload mr-2"></i>
            Import Data
          </button>
        </div>
      </div>
    </div>

    <!-- Comparable Data Calculator -->
    <div class="mb-6">
      <ComparableDataCalculator 
        :connection-status="connectionStatus"
        @log="addToLog"
      />
    </div>

    <!-- SQL Query Interface -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <h3 class="text-lg font-semibold mb-4 flex items-center">
        <i class="fas fa-code mr-2 text-purple-500"></i>
        SQL Query Interface
      </h3>
      <div class="space-y-4">
        <textarea 
          v-model="sqlQuery"
          placeholder="Enter your SQL query here..."
          class="w-full h-32 p-3 border border-gray-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
        ></textarea>
        <div class="flex justify-between items-center">
          <button 
            @click="executeQuery"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            :disabled="!sqlQuery.trim()"
          >
            <i class="fas fa-play mr-2"></i>
            Execute Query
          </button>
          <button 
            @click="clearQuery"
            class="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i class="fas fa-trash mr-2"></i>
            Clear
          </button>
        </div>
      </div>
    </div>

    <!-- Query Results -->
    <div v-if="queryResults.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <h3 class="text-lg font-semibold mb-4 flex items-center">
        <i class="fas fa-table mr-2 text-indigo-500"></i>
        Query Results
      </h3>
      <div class="overflow-x-auto">
        <table class="min-w-full table-auto">
          <thead>
            <tr class="bg-gray-50">
              <th v-for="(header, index) in queryHeaders" :key="index" 
                  class="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in queryResults" :key="index" class="hover:bg-gray-50">
              <td v-for="(cell, cellIndex) in row" :key="cellIndex" 
                  class="px-4 py-2 text-sm text-gray-600 border-b">
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Activity Log -->
    <div v-if="activityLog.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-6">
      <h3 class="text-lg font-semibold mb-4 flex items-center">
        <i class="fas fa-history mr-2 text-yellow-500"></i>
        Activity Log
      </h3>
      <div class="space-y-2 max-h-48 overflow-y-auto">
        <div v-for="(log, index) in activityLog" :key="index" 
             class="text-sm p-2 rounded bg-gray-50 border-l-4"
             :class="log.type === 'success' ? 'border-green-500' : log.type === 'error' ? 'border-red-500' : 'border-blue-500'">
          <span class="text-gray-500">{{ log.timestamp }}</span> - {{ log.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import ComparableDataCalculator from '@/components/ComparableDataCalculator.vue'

// Reactive data
const connectionStatus = ref(false)
const sqlQuery = ref('')
const queryResults = ref([])
const queryHeaders = ref([])
const activityLog = ref([])

// Methods
const toggleConnection = () => {
  connectionStatus.value = !connectionStatus.value
  addToLog(
    connectionStatus.value ? 'Connected to comparable_data database' : 'Disconnected from comparable_data database',
    connectionStatus.value ? 'success' : 'info'
  )
}

const showTables = () => {
  addToLog('Fetching tables from comparable_data database...', 'info')
  // Simulate showing tables
  setTimeout(() => {
    queryHeaders.value = ['Table Name', 'Engine', 'Rows', 'Size']
    queryResults.value = [
      ['comparable_odds', 'InnoDB', '15,432', '2.3 MB'],
      ['comparable_matches', 'InnoDB', '8,765', '1.8 MB'],
      ['comparable_bookmakers', 'InnoDB', '234', '45 KB']
    ]
    addToLog('Tables fetched successfully', 'success')
  }, 1000)
}

const analyzeTable = () => {
  addToLog('Analyzing tables in comparable_data database...', 'info')
  setTimeout(() => {
    addToLog('Table analysis completed', 'success')
  }, 2000)
}

const optimizeTable = () => {
  addToLog('Optimizing tables in comparable_data database...', 'info')
  setTimeout(() => {
    addToLog('Tables optimized successfully', 'success')
  }, 3000)
}

const queryData = () => {
  sqlQuery.value = 'SELECT * FROM comparable_odds LIMIT 10;'
  addToLog('Sample query loaded', 'info')
}

const exportData = () => {
  addToLog('Exporting comparable_data...', 'info')
  setTimeout(() => {
    addToLog('Data exported successfully', 'success')
  }, 2000)
}

const importData = () => {
  addToLog('Importing data to comparable_data...', 'info')
  setTimeout(() => {
    addToLog('Data imported successfully', 'success')
  }, 2500)
}

const executeQuery = () => {
  if (!connectionStatus.value) {
    addToLog('Error: Not connected to database', 'error')
    return
  }
  
  if (!sqlQuery.value.trim()) {
    addToLog('Error: No query to execute', 'error')
    return
  }
  
  addToLog(`Executing query: ${sqlQuery.value.substring(0, 50)}...`, 'info')
  
  // Simulate query execution
  setTimeout(() => {
    queryHeaders.value = ['id', 'match_id', 'bookmaker', 'odds_home', 'odds_away', 'timestamp']
    queryResults.value = [
      ['1', '12345', 'Bet365', '1.85', '2.10', '2024-01-15 14:30:00'],
      ['2', '12346', 'William Hill', '1.90', '2.05', '2024-01-15 14:31:00'],
      ['3', '12347', 'Unibet', '1.88', '2.08', '2024-01-15 14:32:00']
    ]
    addToLog('Query executed successfully', 'success')
  }, 1500)
}

const clearQuery = () => {
  sqlQuery.value = ''
  queryResults.value = []
  queryHeaders.value = []
  addToLog('Query cleared', 'info')
}

const addToLog = (message, type = 'info') => {
  const timestamp = new Date().toLocaleTimeString()
  activityLog.value.unshift({ message, type, timestamp })
  
  // Keep only last 20 logs
  if (activityLog.value.length > 20) {
    activityLog.value = activityLog.value.slice(0, 20)
  }
}
</script> 